import {
  HttpContext,
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SpinnerService } from '../spinner.service';
import { PETICION_SIN_SPINNER } from './peticion-sin-spinner';

/**
 * Marca una petición como silenciosa: no levanta el spinner.
 *
 * A diferencia de `EXCLUIR_RUTAS`, que silencia una ruta para SIEMPRE, esto se decide en cada
 * llamada: la misma URL puede ir con spinner cuando el usuario la está esperando y sin él cuando
 * se precarga en segundo plano.
 */
export const PETICION_SILENCIOSA = new HttpContextToken<boolean>(() => false);

/** Azúcar para `{ context: contextoSilencioso() }` en los servicios. */
export const contextoSilencioso = (): HttpContext =>
  new HttpContext().set(PETICION_SILENCIOSA, true);

const EXCLUIR_RUTAS = [
  {
    URL: '/recepcion_mercancias_detalle/',
    METODO: 'PUT',
  },
  {
    URL: '/api/usuarios-favoritos',
    METODO: 'POST',
  },
  {
    URL: '/api/usuarios-favoritos',
    METODO: 'DELETE',
  },
  {
    URL: '/integrations/clientes/publico_general/',
    METODO: 'GET',
  },
  {
    URL: '/integrations/articulos/escaner/',
    METODO: 'GET',
  },
  {
    URL: '/integrations/articulos/descuento/',
    METODO: 'GET',
  },
  {
    URL: '/wms/traspaso/detalle/agregar-articulo/',
    METODO: 'POST',
  },
  {
    URL: '/wms/traspaso/detalle/actualizar-articulo/',
    METODO: 'PUT',
  },
  {
    URL: '/depositos/monto-pendiente-depositar',
    METODO: 'GET',
  },
  {
    URL: '/wms/traspaso/detalle/desactivar/',
    METODO: 'PUT',
  },
  {
    URL: '/recepcion_tiendas/detalle',
    METODO: 'POST',
  },
  {
    URL: '/recepcion_tiendas/detalle',
    METODO: 'PUT',
  },
  {
    URL: '/wms/monitores/eficiencia-surtido',
    METODO: 'GET',
  },
  {
    // Polling de restante B2B + validación de avance: lecturas frecuentes, sin spinner
    URL: '/wms/remisiones',
    METODO: 'GET',
  },
  {
    // Guardar artículo escaneado B2B (fire-and-forget por cada scan)
    URL: '/wms/remisiones/detalle/',
    METODO: 'POST',
  },
  {
    // Actualizar cantidad escaneada B2B (fire-and-forget)
    URL: '/wms/remisiones/detalle/',
    METODO: 'PUT',
  },
  {
    // Generación de guía autogenerada en la asignación masiva: se resuelve en
    // segundo plano (fila "pendiente") sin bloquear el resto del escaneo.
    URL: '/wms/paqueterias/generar-guia/',
    METODO: 'GET',
  },
  {
    // Liberar reserva de guía autogenerada al quitar un par de la lista
    URL: '/wms/paqueterias/guia-reservada/',
    METODO: 'DELETE',
  },
  {
    // Guardar/eliminar avance de captura (folio+guía manual) en la
    // asignación masiva: fire-and-forget de respaldo por cada par
    // confirmado, no debe bloquear el escaneo continuo con el lector.
    URL: '/wms/paqueterias/avance',
    METODO: 'POST',
  },
  {
    URL: '/wms/paqueterias/avance',
    METODO: 'DELETE',
  },
  {
    // KPIs de solicitudes de traspaso: la card de estadísticas muestra su propio loader.
    URL: '/wms/solicitudes_traspaso/estadisticas',
    METODO: 'POST',
    EXACTA: true,
  },
];

@Injectable({
  providedIn: 'root',
})
export class SpinnerInterceptor implements HttpInterceptor {
  constructor(private servicioSpinner: SpinnerService) {}

  intercept(
    solicitud: HttpRequest<any>,
    siguiente: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (solicitud.context.get(PETICION_SILENCIOSA)) {
      return siguiente.handle(solicitud);
    }

    const excluir = this.excluir_rutas(solicitud);
    if (excluir) {
      return siguiente.handle(solicitud);
    }

    this.servicioSpinner.mostrar();

    return siguiente.handle(solicitud).pipe(
      finalize(() => {
        this.servicioSpinner.ocultar();
      }),
      catchError((error) => {
        this.servicioSpinner.ocultar();
        return throwError(() => error);
      }),
    );
  }

  excluir_rutas = (solicitud: any) => {
    if (solicitud.context?.get(PETICION_SIN_SPINNER)) return true;
    const ruta = new URL(solicitud.url, location.origin).pathname;
    const metodo = solicitud.method;

    const excluir = EXCLUIR_RUTAS.find((rutaExcluir) => {
      const url = rutaExcluir.URL?.toUpperCase();
      const coincide = rutaExcluir.EXACTA
        ? ruta?.toUpperCase().replace(/\/+$/, '') === url
        : ruta?.toUpperCase().startsWith(url);
      return coincide && metodo?.toUpperCase() === rutaExcluir.METODO?.toUpperCase();
    });

    return excluir;
  };
}
